import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface DrillDownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: any[];
  type?: 'chart' | 'table' | 'analysis';
  chartConfig?: {
    xKey: string;
    yKey: string;
    lineKey?: string;
  };
  columns?: Array<{
    field: string;
    headerName: string;
    width?: number;
  }>;
  insights?: Array<{
    id: string;
    title: string;
    description: string;
    severity: 'success' | 'warning' | 'error' | 'info';
    trend?: 'up' | 'down';
  }>;
}

const DrillDownModal: React.FC<DrillDownModalProps> = ({
  open,
  onClose,
  title,
  data,
  type = 'chart',
  chartConfig,
  columns,
  insights
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'success': return 'success.main';
      case 'warning': return 'warning.main';
      case 'error': return 'error.main';
      default: return 'info.main';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Chart View" />
          <Tab label="Table View" />
          <Tab label="Analysis" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Chart View */}
          {activeTab === 0 && chartConfig && (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={chartConfig.xKey} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey={chartConfig.yKey}
                  fill="#8B2438"
                  radius={[8, 8, 0, 0]}
                />
                {chartConfig.lineKey && (
                  <Line
                    type="monotone"
                    dataKey={chartConfig.lineKey}
                    stroke="#ff7300"
                    strokeWidth={2}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}

          {/* Table View */}
          {activeTab === 1 && columns && (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.field} sx={{ fontWeight: 'bold' }}>
                        {col.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow key={index} hover>
                      {columns.map((col) => (
                        <TableCell key={col.field}>
                          {row[col.field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Analysis View */}
          {activeTab === 2 && insights && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Key Insights
              </Typography>
              <List>
                {insights.map((insight) => (
                  <ListItem
                    key={insight.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <InsightsIcon sx={{ color: getSeverityColor(insight.severity) }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {insight.title}
                          </Typography>
                          {insight.trend && (
                            insight.trend === 'up' ? (
                              <TrendingUpIcon color="success" fontSize="small" />
                            ) : (
                              <TrendingDownIcon color="error" fontSize="small" />
                            )
                          )}
                        </Box>
                      }
                      secondary={insight.description}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownModal;
